import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import HeaderPattern from "../Components/Common/HeaderPattern";
import ProjectModal from "../Components/Project/ProjectModal.js";
import { api } from "../config";
import "./Styles/ProgettiStyles.css";



// Map filter keys to API category values (Italian names used by backend)
const filterApiMap = {
    "projects.filters.all": "Tutti",
    "projects.filters.healthcare": "Sanità",
    "projects.filters.education": "Scolastico",
    "projects.filters.ppp": "PPP",
};

const filterKeys = [
    "projects.filters.all",
    "projects.filters.healthcare",
    "projects.filters.education",
    "projects.filters.ppp",
];

export default function ProgettiHeaderResponsive() {
    const { t } = useTranslation();
    const [projects, setProjects] = useState([]);
    const [displayedProjects, setDisplayedProjects] = useState([]);
    const [error, setError] = useState("");
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeFilterKey, setActiveFilterKey] = useState("projects.filters.all");
    const [visibleCount, setVisibleCount] = useState(18);
    const [loading, setLoading] = useState(true);

    const fetchProjects = (filterKey = "projects.filters.all") => {
        const apiCategory = filterApiMap[filterKey];
        let url = api("/getProjects.php");
        if (apiCategory && apiCategory !== "Tutti") url += `?category=${encodeURIComponent(apiCategory)}`;

        setLoading(true);

        fetch(url)
            .then((res) => {
                if (!res.ok) throw new Error("Network response was not ok");
                return res.json();
            })
            .then((data) => {
                const normalized = (Array.isArray(data) ? data : []).map((p) => ({
                    ...p,
                    id: p.id ?? p.overlayText ?? "",
                    titolo: p.titolo ?? p.overlayText ?? "",
                    sottotitolo: p.sottotitolo ?? "",
                    descrizione: p.descrizione ?? p.description ?? "",
                    citta: p.citta ?? p.city ?? "",
                    inizio: p.inizio || "",
                    fine: p.fine || "",
                    categorie: p.categorie || [],
                    categorie_nomi: p.categorie_nomi || [],
                    categoria_id: p.categoria_id || p.category_id || "",
                    category: p.category || "",
                    is_important: p.is_important || p.isImportant || 0,
                    isImportant: Boolean(p.is_important || p.isImportant),
                    images: Array.isArray(p.images) ? p.images : [],
                    heroImage: p.heroImage ?? null
                }));
                setProjects(normalized);
                setDisplayedProjects(normalized.slice(0, visibleCount));
            })
            .catch((err) => {
                setError(t('projects.errorFetch'));
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    function getProjectImage(project) {
        if (Array.isArray(project.images) && project.images.length > 0) {
            const heroImage = project.images.find((img) => img.isHero) || project.images[0];
            const path = heroImage?.path || "";
            if (!path) return "/placeholder-image.jpg";
            if (path.startsWith("http") || path.startsWith("/")) return path;
            return api(`/Progetti/${project.id}/${path}`);
        }
        if (project.heroImage) {
            const path = project.heroImage;
            if (path.startsWith("http") || path.startsWith("/")) return path;
            return api(`/Progetti/${project.id}/${path}`);
        }
        return "/placeholder-image.jpg";
    }

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setDisplayedProjects(projects.slice(0, visibleCount));
    }, [projects, visibleCount]);

    const handleProjectClick = (project) => setSelectedProject(project);
    const closeModal = () => setSelectedProject(null);

    const handleFilterClick = (filterKey) => {
        setActiveFilterKey(filterKey);
        setVisibleCount(18);
        fetchProjects(filterKey);
    };

    const loadMoreProjects = () => {
        setVisibleCount((prev) => prev + 18);
    };

    const hasMoreProjects = projects.length > displayedProjects.length;

    return (
        <>
            <div className="progetti-header-container esi-transition-header">
                <div className="progetti-header-svg-wrapper">
                    <HeaderPattern className="progetti-header-lines" />
                </div>
                <div className="progetti-header-right-zone">
                    <div className="progetti-header-title-container">
                        <h1 className="progetti-header-title">{t('projects.title')}</h1>
                    </div>

                    <div className="progetti-header-filters-wrapper">
                        {filterKeys.map((fKey) => (
                            <button
                                key={fKey}
                                className={`progetti-header-filter-btn${activeFilterKey === fKey ? ' active' : ''}`}
                                style={{ opacity: activeFilterKey === fKey ? 1 : 0.6 }}
                                onClick={() => handleFilterClick(fKey)}
                            >
                                {t(fKey)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* MASONRY GALLERY */}
            <div className="projects-outer-container">
                {error && <div className="alert alert-danger">{error}</div>}

                <Box sx={{ width: "100%", height: "auto", overflowY: "initial", padding: { xs: "0 1rem", sm: "0 1.5rem", md: "0 2rem" } }}>
                    {loading ? (
                        <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
                            <CircularProgress color="error" />
                        </Box>
                    ) : (
                        <>
                            <ImageList variant="masonry" cols={3} gap={16}
                                sx={{ margin: 0, padding: "2rem 0" }}
                                key={`grid-${activeFilterKey}`}
                            >
                                {displayedProjects.map((project) => {
                                    const img = getProjectImage(project);
                                    const alt = project.titolo || project.citta || "project";
                                    return (
                                        <ImageListItem
                                            key={project.id}
                                            onClick={() => handleProjectClick(project)}
                                            sx={{ cursor: "pointer", overflow: "hidden" }}
                                        >
                                            <div className="masonry-image-wrapper">
                                                <img
                                                    src={img}
                                                    alt={alt}
                                                    loading="lazy"
                                                    style={{
                                                        width: "100%",
                                                        display: "block",
                                                    }}
                                                />
                                                <div className="masonry-image-overlay">
                                                    <div className="masonry-overlay-content">
                                                        <p className="masonry-project-title">{project.titolo}</p>
                                                        {project.citta && <p className="masonry-project-city">{project.citta}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        </ImageListItem>
                                    );
                                })}
                            </ImageList>

                            {hasMoreProjects && (
                                <div className="projects-load-more-container">
                                    <button className="projects-load-more-btn" onClick={loadMoreProjects}>
                                        {t('projects.loadMore')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </Box>
            </div>

            {selectedProject && (
                <ProjectModal project={selectedProject} isOpen={!!selectedProject} onClose={closeModal} />
            )}
        </>
    );
}
